import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MapPin,
  NavigationArrow,
  CheckCircle,
  XCircle,
  Clock,
  Plus,
  Car,
  Phone,
  CalendarBlank,
  PencilSimple,
  CaretLeft,
} from "@phosphor-icons/react";
import { formatISTDateTime } from "@/lib/time";

const STATUS_META = {
  pending: { label: "Pending", color: "bg-[#FEF3C7] text-[#92400E]", icon: Clock },
  booked: { label: "Booked", color: "bg-[#D1FAE5] text-[#065F46]", icon: CheckCircle },
  cancelled: { label: "Cancelled", color: "bg-[#FEE2E2] text-[#991B1B]", icon: XCircle },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "booked", label: "Booked" },
  { key: "cancelled", label: "Cancelled" },
];

const CAB_TYPES = [
  { value: "mini", label: "Mini" },
  { value: "sedan", label: "Sedan" },
  { value: "suv", label: "SUV" },
];

function mapsDirectionUrl(pickup, drop) {
  const parts = [];
  if (pickup?.lat != null && pickup?.lng != null) parts.push(`${pickup.lat},${pickup.lng}`);
  else if (pickup?.address) parts.push(encodeURIComponent(pickup.address));
  if (drop?.lat != null && drop?.lng != null) parts.push(`${drop.lat},${drop.lng}`);
  else if (drop?.address) parts.push(encodeURIComponent(drop.address));
  if (parts.length < 2) return null;
  return `https://www.google.com/maps/dir/?api=1&origin=${parts[0]}&destination=${parts[1]}&travelmode=driving`;
}

export default function BookingsDialog({
  open,
  onClose,
  prefillPhone = "",
  prefillName = "",
  defaultFilter = "pending",
  fullPage = false,
}) {
  const [view, setView] = useState("list"); // list | create | detail
  const [filter, setFilter] = useState(defaultFilter);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateField, setDateField] = useState("created_at");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, pending: 0, booked: 0, cancelled: 0 });
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { status: filter, q: search || undefined, limit: 200 };
      if (dateFrom) {
        params.date_from = new Date(dateFrom).toISOString();
      }
      if (dateTo) {
        const d = new Date(dateTo);
        d.setHours(23, 59, 59, 999);
        params.date_to = d.toISOString();
      }
      if (dateFrom || dateTo) {
        params.date_field = dateField;
      }
      const r = await api.get("/bookings", { params });
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (_) {
      toast.error("Bookings load nahi huye");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (prefillPhone) setView("create");
    else setView("list");
  }, [open, prefillPhone]);

  useEffect(() => {
    if (!open || view !== "list") return;
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, view, filter, search, dateFrom, dateTo, dateField]);

  const reopenList = () => {
    setActive(null);
    setView("list");
  };

  const header = (
    <div className="px-4 md:px-6 py-4 border-b border-[var(--border)] bg-[var(--surface)]">
      <div className="flex items-center gap-2">
        {view !== "list" && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 -ml-2"
            onClick={reopenList}
            data-testid="bookings-back"
          >
            <CaretLeft size={18} weight="bold" />
          </Button>
        )}
        <div className="flex-1 min-w-0">
          <div className="heading font-semibold text-[16px] md:text-[18px] leading-tight">
            {view === "list" && "Bookings"}
            {view === "create" && "New Booking"}
            {view === "detail" && `Booking ${active?.booking_id || ""}`}
          </div>
          <div className="text-xs text-[var(--muted)] leading-tight truncate">
            {view === "list" && "Customer leads — team confirm kare aur old server pe book kare."}
            {view === "create" && "Customer ke liye naya booking lead record karein."}
            {view === "detail" && "Details check karke status update karein."}
          </div>
        </div>
        {view === "list" && (
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => setView("create")}
            data-testid="new-booking-btn"
          >
            <Plus size={14} weight="bold" /> New
          </Button>
        )}
      </div>
    </div>
  );

  const body = (
    <div className="flex-1 overflow-y-auto">
      {view === "list" && (
        <BookingList
          items={items}
          counts={counts}
          filter={filter}
          setFilter={setFilter}
          search={search}
          setSearch={setSearch}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          dateField={dateField}
          setDateField={setDateField}
          loading={loading}
          onOpen={(b) => {
            setActive(b);
            setView("detail");
          }}
        />
      )}

      {view === "create" && (
        <BookingCreateForm
          initialPhone={prefillPhone}
          initialName={prefillName}
          onCancel={reopenList}
          onCreated={(b) => {
            toast.success(`Booking ${b.booking_id} created`);
            setActive(b);
            setView("detail");
          }}
        />
      )}

      {view === "detail" && active && (
        <BookingDetail
          booking={active}
          onChanged={(b) => setActive(b)}
          onBack={reopenList}
        />
      )}
    </div>
  );

  if (fullPage) {
    if (!open) return null;
    return (
      <div
        className="h-full w-full flex flex-col bg-[var(--bg)] text-[var(--fg)]"
        data-testid="bookings-dialog"
      >
        {header}
        {body}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col p-0"
        data-testid="bookings-dialog"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Bookings</DialogTitle>
          <DialogDescription>Manage bookings</DialogDescription>
        </DialogHeader>
        {header}
        {body}
      </DialogContent>
    </Dialog>
  );
}

function BookingList({
  items,
  counts,
  filter,
  setFilter,
  search,
  setSearch,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  dateField,
  setDateField,
  loading,
  onOpen,
}) {
  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-md text-xs transition-colors ${
              filter === f.key
                ? "bg-[var(--fg)] text-[var(--bg)]"
                : "hover:bg-[var(--bg)] text-[var(--muted)] border border-[var(--border)]"
            }`}
            data-testid={`booking-filter-${f.key}`}
          >
            {f.label} <span className="opacity-70 ml-1">{counts?.[f.key] ?? 0}</span>
          </button>
        ))}
        <div className="flex-1 min-w-[180px]">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search booking ID, phone, name, city…"
            className="h-8 text-xs"
            data-testid="booking-search"
          />
        </div>
      </div>

      {/* Date filter row */}
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="text-[var(--muted)]">Date:</span>
        <Select value={dateField} onValueChange={setDateField}>
          <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="booking-date-field">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="created_at">Created</SelectItem>
            <SelectItem value="travel_datetime">Travel</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="h-8 text-xs w-[150px]"
          data-testid="booking-date-from"
        />
        <span className="text-[var(--muted)]">to</span>
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="h-8 text-xs w-[150px]"
          data-testid="booking-date-to"
        />
        {(dateFrom || dateTo) && (
          <button
            onClick={() => {
              setDateFrom("");
              setDateTo("");
            }}
            className="text-xs px-2 py-1 rounded hover:bg-[var(--bg)] text-[var(--muted)]"
            data-testid="booking-clear-dates"
          >
            Clear
          </button>
        )}
      </div>

      {loading && <div className="text-center text-xs text-[var(--muted)] py-6">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-center text-sm text-[var(--muted)] py-10">
          Koi booking nahi mili.
        </div>
      )}

      <ul className="space-y-2">
        {items.map((b) => {
          const S = STATUS_META[b.status] || STATUS_META.pending;
          const Icon = S.icon;
          return (
            <li
              key={b.booking_id}
              className="border border-[var(--border)] rounded-lg p-3 hover:bg-[var(--bg)] cursor-pointer transition-colors"
              onClick={() => onOpen(b)}
              data-testid={`booking-row-${b.booking_id}`}
            >
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold text-sm">{b.booking_id}</span>
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${S.color}`}>
                      <Icon size={10} weight="fill" /> {S.label}
                    </span>
                    <span className="text-[10px] text-[var(--muted)] uppercase">{b.cab_type}</span>
                  </div>
                  <div className="text-sm mt-1 font-medium truncate">
                    {b.customer_name || b.customer_phone} · <span className="font-mono text-xs text-[var(--muted)]">+{b.customer_phone}</span>
                  </div>
                  <div className="text-xs text-[var(--muted)] mt-1 flex items-center gap-1 flex-wrap">
                    <MapPin size={12} />
                    <span className="truncate">{b.pickup?.city || b.pickup?.address}</span>
                    <NavigationArrow size={10} weight="bold" />
                    <span className="truncate">{b.drop?.city || b.drop?.address}</span>
                  </div>
                  <div className="text-[11px] text-[var(--muted)] mt-1 flex items-center gap-3">
                    {b.travel_datetime && (
                      <span className="flex items-center gap-1">
                        <CalendarBlank size={11} /> {formatISTDateTime(b.travel_datetime)}
                      </span>
                    )}
                    {b.fare_estimated != null && <span>₹{Number(b.fare_estimated).toLocaleString("en-IN")}</span>}
                    <span className="ml-auto">{formatISTDateTime(b.created_at)}</span>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function BookingCreateForm({ initialPhone, initialName, onCancel, onCreated }) {
  const [f, setF] = useState({
    customer_phone: initialPhone || "",
    customer_name: initialName || "",
    pickup_city: "",
    pickup_address: "",
    pickup_lat: "",
    pickup_lng: "",
    drop_city: "",
    drop_address: "",
    drop_lat: "",
    drop_lng: "",
    travel_datetime: "",
    cab_type: "sedan",
    distance_km: "",
    fare_estimated: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...f,
        customer_phone: f.customer_phone.replace(/\D/g, ""),
        pickup_lat: f.pickup_lat === "" ? null : Number(f.pickup_lat),
        pickup_lng: f.pickup_lng === "" ? null : Number(f.pickup_lng),
        drop_lat: f.drop_lat === "" ? null : Number(f.drop_lat),
        drop_lng: f.drop_lng === "" ? null : Number(f.drop_lng),
        distance_km: f.distance_km === "" ? null : Number(f.distance_km),
        fare_estimated: f.fare_estimated === "" ? null : Number(f.fare_estimated),
        travel_datetime: f.travel_datetime ? new Date(f.travel_datetime).toISOString() : null,
        source: "dashboard",
      };
      const r = await api.post("/bookings", body);
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-5 space-y-4" data-testid="create-booking-form">
      <Section title="Customer">
        <Field label="Phone (with country code, no +)" required>
          <Input
            required
            inputMode="numeric"
            value={f.customer_phone}
            onChange={set("customer_phone")}
            placeholder="919876543210"
            data-testid="bk-customer-phone"
          />
        </Field>
        <Field label="Name">
          <Input value={f.customer_name} onChange={set("customer_name")} data-testid="bk-customer-name" />
        </Field>
      </Section>

      <Section title="Pickup">
        <Field label="City">
          <Input value={f.pickup_city} onChange={set("pickup_city")} placeholder="Patna" />
        </Field>
        <Field label="Address" required full>
          <Input required value={f.pickup_address} onChange={set("pickup_address")} placeholder="Gandhi Maidan, Patna" />
        </Field>
        <Field label="Pickup Latitude">
          <Input inputMode="decimal" value={f.pickup_lat} onChange={set("pickup_lat")} placeholder="25.6127" />
        </Field>
        <Field label="Pickup Longitude">
          <Input inputMode="decimal" value={f.pickup_lng} onChange={set("pickup_lng")} placeholder="85.1415" />
        </Field>
      </Section>

      <Section title="Drop">
        <Field label="City">
          <Input value={f.drop_city} onChange={set("drop_city")} placeholder="Delhi" />
        </Field>
        <Field label="Address" required full>
          <Input required value={f.drop_address} onChange={set("drop_address")} placeholder="Connaught Place, Delhi" />
        </Field>
        <Field label="Drop Latitude">
          <Input inputMode="decimal" value={f.drop_lat} onChange={set("drop_lat")} placeholder="28.6330" />
        </Field>
        <Field label="Drop Longitude">
          <Input inputMode="decimal" value={f.drop_lng} onChange={set("drop_lng")} placeholder="77.2190" />
        </Field>
      </Section>

      <Section title="Trip">
        <Field label="Travel date & time">
          <Input
            type="datetime-local"
            value={f.travel_datetime}
            onChange={set("travel_datetime")}
            data-testid="bk-datetime"
          />
        </Field>
        <Field label="Cab type">
          <Select value={f.cab_type} onValueChange={(v) => setF((p) => ({ ...p, cab_type: v }))}>
            <SelectTrigger data-testid="bk-cab-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CAB_TYPES.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Distance (km)">
          <Input inputMode="decimal" value={f.distance_km} onChange={set("distance_km")} placeholder="1089" />
        </Field>
        <Field label="Fare estimate (₹)">
          <Input inputMode="decimal" value={f.fare_estimated} onChange={set("fare_estimated")} placeholder="13868" />
        </Field>
        <Field label="Notes" full>
          <Textarea
            value={f.notes}
            onChange={set("notes")}
            placeholder="Special requests, pickup instructions…"
            className="min-h-[60px]"
          />
        </Field>
      </Section>

      <div className="flex items-center gap-2 justify-end pt-2 border-t border-[var(--border)]">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving} className="gap-1.5" data-testid="bk-submit">
          <Plus size={14} weight="bold" />
          {saving ? "Saving…" : "Create booking"}
        </Button>
      </div>
    </form>
  );
}

function BookingDetail({ booking, onChanged, onBack }) {
  const [notes, setNotes] = useState(booking.notes || "");
  const [ref, setRef] = useState(booking.external_booking_ref || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setNotes(booking.notes || "");
    setRef(booking.external_booking_ref || "");
  }, [booking.booking_id]);

  const mapsUrl = useMemo(() => mapsDirectionUrl(booking.pickup, booking.drop), [booking]);
  const S = STATUS_META[booking.status] || STATUS_META.pending;

  const update = async (status) => {
    setBusy(true);
    try {
      const r = await api.patch(`/bookings/${booking.booking_id}/status`, {
        status,
        notes,
        external_booking_ref: ref || null,
      });
      onChanged(r.data);
      toast.success(`Booking ${status}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-5 space-y-5" data-testid="booking-detail">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-mono text-lg font-semibold">{booking.booking_id}</span>
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full uppercase tracking-wider ${S.color}`}>
          {S.label}
        </span>
        <span className="text-xs text-[var(--muted)] ml-auto">
          Created {formatISTDateTime(booking.created_at)}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <InfoCard icon={Phone} title="Customer">
          <div className="font-medium">{booking.customer_name || "—"}</div>
          <div className="text-xs text-[var(--muted)] font-mono">+{booking.customer_phone}</div>
        </InfoCard>
        <InfoCard icon={Car} title="Cab & Fare">
          <div className="uppercase text-xs tracking-wider">{booking.cab_type}</div>
          <div className="text-xs text-[var(--muted)]">
            {booking.distance_km != null && `${booking.distance_km} km`}
            {booking.distance_km != null && booking.fare_estimated != null && " · "}
            {booking.fare_estimated != null && `₹${Number(booking.fare_estimated).toLocaleString("en-IN")}`}
          </div>
        </InfoCard>
        <InfoCard icon={MapPin} title="Pickup">
          <div className="font-medium text-sm">{booking.pickup?.city || "—"}</div>
          <div className="text-xs text-[var(--muted)]">{booking.pickup?.address}</div>
          {booking.pickup?.lat != null && (
            <div className="text-[10px] text-[var(--muted)] font-mono mt-1">
              {booking.pickup.lat}, {booking.pickup.lng}
            </div>
          )}
        </InfoCard>
        <InfoCard icon={NavigationArrow} title="Drop">
          <div className="font-medium text-sm">{booking.drop?.city || "—"}</div>
          <div className="text-xs text-[var(--muted)]">{booking.drop?.address}</div>
          {booking.drop?.lat != null && (
            <div className="text-[10px] text-[var(--muted)] font-mono mt-1">
              {booking.drop.lat}, {booking.drop.lng}
            </div>
          )}
        </InfoCard>
      </div>

      {booking.travel_datetime && (
        <div className="text-sm text-[var(--muted)] flex items-center gap-2">
          <CalendarBlank size={14} /> Travel: {formatISTDateTime(booking.travel_datetime)}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {mapsUrl ? (
          <Button asChild variant="outline" size="sm" className="gap-1.5">
            <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid="view-on-maps">
              <NavigationArrow size={14} weight="fill" /> View route on Google Maps
            </a>
          </Button>
        ) : (
          <span className="text-[11px] text-[var(--muted)]">Lat/lng dono jagah set karke map dekh sakte hain.</span>
        )}
      </div>

      <div className="border-t border-[var(--border)] pt-4 space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Notes (internal)</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Team notes, pickup instructions, cancel reason…"
            className="min-h-[60px]"
            data-testid="booking-notes"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Old server booking reference (optional)</Label>
          <Input
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder="e.g. RB-2647"
            data-testid="booking-external-ref"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap pt-2">
          <Button
            size="sm"
            className="gap-1.5 bg-[#10B981] hover:bg-[#059669] text-white"
            onClick={() => update("booked")}
            disabled={busy || booking.status === "booked"}
            data-testid="mark-booked-btn"
          >
            <CheckCircle size={14} weight="fill" /> Mark Booked
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => update("pending")}
            disabled={busy || booking.status === "pending"}
            data-testid="mark-pending-btn"
          >
            <Clock size={14} weight="fill" /> Mark Pending
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-[#DC2626] border-[#FCA5A5] hover:bg-[#FEE2E2]"
            onClick={() => update("cancelled")}
            disabled={busy || booking.status === "cancelled"}
            data-testid="mark-cancelled-btn"
          >
            <XCircle size={14} weight="fill" /> Cancel
          </Button>
        </div>
      </div>

      {booking.status_history?.length > 1 && (
        <div className="border-t border-[var(--border)] pt-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--muted)] mb-2">
            History
          </div>
          <ul className="space-y-1.5">
            {booking.status_history.slice().reverse().map((h, i) => {
              const HS = STATUS_META[h.status] || STATUS_META.pending;
              return (
                <li key={i} className="text-xs flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded ${HS.color} uppercase text-[9px]`}>{HS.label}</span>
                  <span className="text-[var(--muted)]">{formatISTDateTime(h.at)}</span>
                  <span className="text-[var(--muted)]">· {h.by}</span>
                  {h.notes && <span className="text-[var(--muted)] truncate">— {h.notes}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-[var(--muted)] mb-2">
        {title}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Field({ label, children, required, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}>
      <Label className="text-xs">
        {label} {required && <span className="text-[#DC2626]">*</span>}
      </Label>
      {children}
    </div>
  );
}

function InfoCard({ icon: Icon, title, children }) {
  return (
    <div className="border border-[var(--border)] rounded-lg p-3 bg-[var(--surface)]">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[var(--muted)] mb-1.5">
        <Icon size={12} /> {title}
      </div>
      {children}
    </div>
  );
}
